import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
} from 'reactstrap';
import MDEditor from '@uiw/react-md-editor';
import LeagueTeams from './LeagueTeams';
import LeagueWeeks from './LeagueWeeks';
import LeagueSchedules from './LeagueSchedules';
import LeagueAdmins from './LeagueAdmins';
import LeagueTeeChoices from './LeagueTeeChoices';
import api from '../../services/api';

const normalizeScoringType = (value) => {
  const v = String(value || '').toLowerCase();
  if (v === 'player') return 'Player';
  if (v === 'team') return 'Team';
  return 'None';
};

const EditLeagueModal = ({ isOpen, toggle, onSave, league, services }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [teamCount, setTeamCount] = useState(0);
  const [editedLeague, setEditedLeague] = useState({
    title: '',
    description: '',
    notes_text: '',
    service_id: '',
    state: 'New',
    sort_order: 99,
    lock_resources: false,
    show_leaderboard: false,
    scoring_type: 'None',
    payment_link: '',
    payment_link_split: '',
    payment_link_text: '',
    payment_link_split_text: '',
    prize_payment_link: '',
    prize_payment_link_split: '',
    prize_payment_link_text: '',
    prize_payment_link_split_text: ''
  });

  useEffect(() => {
    if (league) {
      setEditedLeague({
        title: league.title || '',
        description: league.description || '',
        notes_text: league.notes_text || '',
        service_id: league.service?.id || '',
        state: league.state || 'New',
        sort_order: league.sort_order || 99,
        lock_resources: Boolean(league.lock_resources),
        show_leaderboard: Boolean(league.show_leaderboard),
        scoring_type: normalizeScoringType(league.scoring_type),
        payment_link: league.payment_link || '',
        payment_link_split: league.payment_link_split || '',
        payment_link_text: league.payment_link_text || '',
        payment_link_split_text: league.payment_link_split_text || '',
        prize_payment_link: league.prize_payment_link || '',
        prize_payment_link_split: league.prize_payment_link_split || '',
        prize_payment_link_text: league.prize_payment_link_text || '',
        prize_payment_link_split_text: league.prize_payment_link_split_text || ''
      });
    }
  }, [league]);

  useEffect(() => {
    // Reset to details tab when modal opens
    if (isOpen) {
      setActiveTab('details');
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchTeamCount = async () => {
      if (league?.id) {
        try {
          const response = await api.get(`/league-teams/?league=${league.id}`);
          setTeamCount(response.data.length);
        } catch (error) {
          console.error('Error fetching team count:', error);
          setTeamCount(0);
        }
      } else {
        setTeamCount(0);
      }
    };
    fetchTeamCount();
  }, [league?.id, isOpen]);

  const handleTeamCountChange = (count) => {
    setTeamCount(count);
  };

  const handleSave = () => {
    onSave(editedLeague);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedLeague(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : name === 'sort_order'
          ? parseInt(value) || 99
          : value
    }));
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>Edit League: {league?.title}</ModalHeader>
      <ModalBody>
        <Nav tabs>
          <NavItem>
            <NavLink
              className={activeTab === 'details' ? 'active' : ''}
              onClick={() => toggleTab('details')}
              style={{ cursor: 'pointer' }}
            >
              Details
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'admins' ? 'active' : ''}
              onClick={() => toggleTab('admins')}
              style={{ cursor: 'pointer' }}
            >
              Admins
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'weeks' ? 'active' : ''}
              onClick={() => toggleTab('weeks')}
              style={{ cursor: 'pointer' }}
            >
              Weeks
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'schedules' ? 'active' : ''}
              onClick={() => toggleTab('schedules')}
              style={{ cursor: 'pointer' }}
            >
              Schedules
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'teams' ? 'active' : ''}
              onClick={() => toggleTab('teams')}
              style={{ cursor: 'pointer' }}
            >
              Teams ({teamCount})
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'tees' ? 'active' : ''}
              onClick={() => toggleTab('tees')}
              style={{ cursor: 'pointer' }}
            >
              Tee Choices
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} className="mt-3">
          <TabPane tabId="details">
            <FormGroup>
              <Label for="title">Title *</Label>
              <Input
                type="text"
                id="title"
                name="title"
                value={editedLeague.title}
                onChange={handleChange}
                placeholder="League title"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="description">Description</Label>
              <MDEditor
                value={editedLeague.description}
                onChange={(value) => setEditedLeague(prev => ({ ...prev, description: value || '' }))}
                preview="edit"
                height={300}
              />
            </FormGroup>
            <FormGroup>
              <Label for="notes_text">Additional Notes Text</Label>
              <Input
                type="textarea"
                id="notes_text"
                name="notes_text"
                value={editedLeague.notes_text}
                onChange={handleChange}
                placeholder="Additional information shown to players on the registration form..."
                rows="4"
              />
            </FormGroup>
            <FormGroup>
              <Label for="service_id">Service *</Label>
              <Input
                type="select"
                id="service_id"
                name="service_id"
                value={editedLeague.service_id}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Service --</option>
                {services.map((svc) => (
                  <option key={svc.id} value={svc.id}>
                    {svc.title} ({svc.duration} min)
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup check className="mb-3">
              <Input
                type="checkbox"
                id="lock_resources"
                name="lock_resources"
                checked={editedLeague.lock_resources}
                onChange={handleChange}
              />
              <Label check for="lock_resources">
                Lock resources for this league
              </Label>
            </FormGroup>
            <FormGroup check className="mb-3">
              <Input
                type="checkbox"
                id="show_leaderboard"
                name="show_leaderboard"
                checked={editedLeague.show_leaderboard}
                onChange={handleChange}
              />
              <Label check for="show_leaderboard">
                Show leaderboard for this league
              </Label>
            </FormGroup>
            <FormGroup>
              <Label for="scoring_type">Scoring Type</Label>
              <Input
                type="select"
                id="scoring_type"
                name="scoring_type"
                value={editedLeague.scoring_type}
                onChange={handleChange}
              >
                <option value="None">None</option>
                <option value="Team">Team</option>
                <option value="Player">Player</option>
              </Input>
              <small className="text-muted">
                Team: one score per team; Player: individual scores per player; None: scoring disabled
              </small>
            </FormGroup>
            <FormGroup>
              <Label for="state">State</Label>
              <Input
                type="select"
                id="state"
                name="state"
                value={editedLeague.state}
                onChange={handleChange}
              >
                <option value="New">New</option>
                <option value="Registration">Registration</option>
                <option value="Active">Active</option>
                <option value="Archived">Archived</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="sort_order">Sort Order</Label>
              <Input
                type="number"
                id="sort_order"
                name="sort_order"
                value={editedLeague.sort_order}
                onChange={handleChange}
                placeholder="99"
              />
            </FormGroup>
            <FormGroup>
              <Label>Payment Links</Label>
              <Card>
                <CardHeader>
                  Payment Link (Full)
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="4">
                      <Label for="payment_link_text">Text</Label>
                      <Input
                        type="text"
                        id="payment_link_text"
                        name="payment_link_text"
                        value={editedLeague.payment_link_text}
                        onChange={handleChange}
                        placeholder="Team Registration"
                      />
                    </Col>
                    <Col md="8">
                      <Label for="payment_link">Link</Label>
                      <Input
                        type="url"
                        id="payment_link"
                        name="payment_link"
                        value={editedLeague.payment_link}
                        onChange={handleChange}
                        placeholder="https://..."
                      />
                      <small className="text-muted">Optional URL for full team payment</small>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </FormGroup>
            <FormGroup>
              <Card>
                <CardHeader>
                  Payment Link (Split)
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="4">
                      <Label for="payment_link_split_text">Text</Label>
                      <Input
                        type="text"
                        id="payment_link_split_text"
                        name="payment_link_split_text"
                        value={editedLeague.payment_link_split_text}
                        onChange={handleChange}
                        placeholder="Player Registration"
                      />
                    </Col>
                    <Col md="8">
                      <Label for="payment_link_split">Link</Label>
                      <Input
                        type="url"
                        id="payment_link_split"
                        name="payment_link_split"
                        value={editedLeague.payment_link_split}
                        onChange={handleChange}
                        placeholder="https://..."
                      />
                      <small className="text-muted">Optional URL for split per-player payments</small>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </FormGroup>
            <FormGroup>
              <Card>
                <CardHeader>
                  Prize Payment Link (Full)
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="4">
                      <Label for="prize_payment_link_text">Text</Label>
                      <Input
                        type="text"
                        id="prize_payment_link_text"
                        name="prize_payment_link_text"
                        value={editedLeague.prize_payment_link_text}
                        onChange={handleChange}
                        placeholder="Team Prize Pool"
                      />
                    </Col>
                    <Col md="8">
                      <Label for="prize_payment_link">Link</Label>
                      <Input
                        type="url"
                        id="prize_payment_link"
                        name="prize_payment_link"
                        value={editedLeague.prize_payment_link}
                        onChange={handleChange}
                        placeholder="https://..."
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </FormGroup>
            <FormGroup>
              <Card>
                <CardHeader>
                  Prize Payment Link (Split)
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="4">
                      <Label for="prize_payment_link_split_text">Text</Label>
                      <Input
                        type="text"
                        id="prize_payment_link_split_text"
                        name="prize_payment_link_split_text"
                        value={editedLeague.prize_payment_link_split_text}
                        onChange={handleChange}
                        placeholder="Player Prize Pool"
                      />
                    </Col>
                    <Col md="8">
                      <Label for="prize_payment_link_split">Link</Label>
                      <Input
                        type="url"
                        id="prize_payment_link_split"
                        name="prize_payment_link_split"
                        value={editedLeague.prize_payment_link_split}
                        onChange={handleChange}
                        placeholder="https://..."
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </FormGroup>
          </TabPane>

          <TabPane tabId="teams">
            {league && <LeagueTeams league={league} onTeamCountChange={handleTeamCountChange} />}
          </TabPane>

          <TabPane tabId="weeks">
            {league && <LeagueWeeks league={league} />}
          </TabPane>

          <TabPane tabId="schedules">
            {league && <LeagueSchedules league={league} />}
          </TabPane>

          <TabPane tabId="admins">
            {league && <LeagueAdmins league={league} />}
          </TabPane>

          <TabPane tabId="tees">
            {league && <LeagueTeeChoices league={league} />}
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter>
        {activeTab === 'details' && (
          <>
            <Button color="primary" onClick={handleSave} disabled={!editedLeague.title || !editedLeague.service_id}>
              Save Changes
            </Button>{' '}
          </>
        )}
        <Button color="secondary" onClick={toggle}>
          {activeTab === 'details' ? 'Cancel' : 'Close'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditLeagueModal;
